// src/context/AppContext.js
import { createContext, useState, useEffect } from 'react';
import { syncDeviceInfo } from './syncDeviceInfo.js';
import { api } from '../api/index.js';
import { statusDetector } from '../utils/statusDetector';
import { openDB } from 'idb';

export const AppContext = createContext();

export const AppProvider = ({ children }) => {
  const [response, setResponse] = useState({
    customer_feedback_id: null,
    question1: { device_id: '', response_value: '', datetime_: null },
    question2: { device_id: '', response_value: '', datetime_: null },
    question3: { device_id: '', response_value: '', datetime_: null },
  });

  // ======== INITIAL LOAD FROM localStorage ========
  const initialDeck = (() => {
    try {
      const raw = localStorage.getItem('deck_data');
      return raw ? JSON.parse(raw) : [];
    } catch (e) {
      console.error('parse deck_data failed', e);
      return [];
    }
  })();
  const [deck, setDeck] = useState(initialDeck);

  const initialAdBanners = (() => {
    try {
      const raw = localStorage.getItem('ad_banners_data');
      return raw ? JSON.parse(raw) : [];
    } catch (e) {
      console.error('parse ad_banners_data failed', e);
      return [];
    }
  })();
  const [adBanners, setAdBanners] = useState(initialAdBanners);
  const [adsEnabled, setAdsEnabled] = useState(() => localStorage.getItem('ads_enabled') === 'true');

  const initialComplaintOptions = (() => {
    try {
      const raw = localStorage.getItem('complaint_options_data');
      return raw ? JSON.parse(raw) : [];
    } catch (e) {
      console.error('parse complaint_options_data failed', e);
      return [];
    }
  })();
  const [complaintOptions, setComplaintOptions] = useState(initialComplaintOptions);
  const [complaintSlideEnabled, setComplaintSlideEnabled] = useState(() => localStorage.getItem('complaint_slide_enabled') === 'true');

  // ---------------- Helper ----------------
  const getLatestDeck = () => {
    try {
      const raw = localStorage.getItem('deck_data');
      if (raw) return JSON.parse(raw);
    } catch (e) {
      console.error('getLatestDeck parse error', e);
    }
    return Array.isArray(deck) ? deck : [];
  };

  // ---------------- FETCH DECKS ----------------
  const fetchDecks = async () => {
    try {
      const device_id = localStorage.getItem('device_id');
      if (!device_id) return;

      const lastUpdated = localStorage.getItem('deck_last_updated');
      const url = `${api}/tablet-config?device_id=${device_id}${lastUpdated ? `&last_updated=${encodeURIComponent(lastUpdated)}` : ''}`;

      const res = await fetch(url, { signal: AbortSignal.timeout(10000) });

      if (res.status === 204 || res.status === 404) return;
      if (!res.ok) {
        statusDetector.activeIssues.add('backend-5xx');
        return;
      }

      const data = await res.json();
      if (data?.slides?.length) {
        setDeck(data);
        localStorage.setItem('deck_data', JSON.stringify(data));
        if (data.last_updated) localStorage.setItem('deck_last_updated', data.last_updated);
      }
    } catch (err) {
      console.error('[fetchDecks] failed', err);
    }
  };

  // ---------------- FETCH AD BANNERS ----------------
  const fetchAdBanners = async (deckToUse) => {
    try {
      const device_id = localStorage.getItem('device_id');
      if (!device_id) return;

      const currentDeck = Array.isArray(deckToUse) && deckToUse.length ? deckToUse : getLatestDeck();
      const deck_id = currentDeck?.[0]?.deck_id;
      if (!deck_id) return;

      const url = `${api}/ads/deck/${deck_id}/banners`;
      const res = await fetch(url, { signal: AbortSignal.timeout(5000) });
      if (!res.ok) return;

      const data = await res.json();
      const ads = data.ads || [];
      const enabled = data.ads_enabled === true;

      setAdBanners(ads);
      setAdsEnabled(enabled);

      localStorage.setItem('ad_banners_data', JSON.stringify(ads));
      localStorage.setItem('ads_enabled', enabled.toString());
    } catch (err) {
      console.error('[fetchAdBanners] failed', err);
    }
  };

  // ---------------- FETCH COMPLAINT OPTIONS ----------------
  const fetchComplaintOptions = async (deckToUse) => {
    console.log('fetchComplaintOptions called with deckToUse:', deckToUse);
    try {
      const device_id = localStorage.getItem('device_id');
      if (!device_id) return;

      const currentDeck = Array.isArray(deckToUse) && deckToUse.length ? deckToUse : getLatestDeck();
      console.log('Current deck for fetching complaint options:', currentDeck);
      const deck_id = currentDeck?.deck_id;
      console.log('fetchComplaintOptions using deck_id:', deck_id);
      if (!deck_id) return;

      const lastUpdated = localStorage.getItem('complaint_options_last_updated');
      const url = `${api}/complaint-options/${deck_id}${lastUpdated ? `?last_updated=${encodeURIComponent(lastUpdated)}` : ''}`;
      const res = await fetch(url, { signal: AbortSignal.timeout(5000) });
      console.log(`fetching complaint options from ${url}, res: ${res}`);
      if (res.status === 204 || !res.ok) return;

      const data = await res.json();
      const complaints = data.complaints || [];
      const isActive = Boolean(data.is_active);

      setComplaintOptions(complaints);
      setComplaintSlideEnabled(isActive);

      localStorage.setItem('complaint_options_data', JSON.stringify(complaints));
      localStorage.setItem('complaint_slide_enabled', isActive.toString());
      localStorage.setItem('complaint_options_last_updated', new Date().toISOString());
    } catch (err) {
      console.error('[fetchComplaintOptions] failed', err);
    }
  };

  // ---------------- REGISTER DEVICE (QR & Manual) ----------------
  const registerDevice = async (payload) => {
    try {
      const db = await openDB('FDBK', 1, {
        upgrade(db) {
          if (!db.objectStoreNames.contains('devices')) {
            db.createObjectStore('devices', { keyPath: 'device_id' });
          }
        }
      });

      let device_id = payload.device_id || localStorage.getItem('device_id');
      if (!device_id) {
        device_id = crypto.randomUUID().replace(/-/g, '').substring(0, 16);
      }

      const siteData = {
        device_id,
        device_name: payload.device_name || localStorage.getItem('device_name') || 'FDBK Device',
        store_setup_code: payload.store_setup_code || '',
        latitude: payload.latitude || null,
        longitude: payload.longitude || null,
      };

      // Call backend /device route if store_setup_code or device_id + store_id is present
      let resData = { status: 'pending' };
      if (siteData.store_setup_code || payload.store_id) {
        const response = await fetch(`${api}/device`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...siteData, store_id: payload.store_id }),
        });
        resData = await response.json();
      }

      // Persist device info to IndexedDB
      const tx = db.transaction('devices', 'readwrite');
      const store = tx.objectStore('devices');
      await store.put({ device_id, store_id: resData.store_id || payload.store_id, business_id: payload.business_id || null });
      await tx.done;

      // Persist to localStorage
      localStorage.setItem('device_id', device_id);
      localStorage.setItem('store_id', resData.store_id || payload.store_id || '');
      localStorage.setItem('device_name', siteData.device_name);

      // Optionally fetch deck and related data immediately after registration
      await fetchDecks();
      const latestDeck = getLatestDeck();
      await fetchAdBanners(latestDeck);
      await fetchComplaintOptions(latestDeck);

      return resData;
    } catch (err) {
      console.error('registerDevice failed', err);
      return { status: 'error', message: err.message };
    }
  };

  // ---------------- useEffect: init + interval ----------------
  useEffect(() => {
    let intervalId = null;

    (async () => {
      try {
        await syncDeviceInfo();
        await fetchDecks();
        const latestDeck = getLatestDeck();
        await fetchAdBanners(latestDeck);
        await fetchComplaintOptions(latestDeck);
      } catch (e) {
        console.error('Startup sequence failed', e);
      }
    })();

    intervalId = setInterval(async () => {
      try {
        await syncDeviceInfo();
        await fetchDecks();
        const latestDeck = getLatestDeck();
        await fetchAdBanners(latestDeck);
        await fetchComplaintOptions(latestDeck);
      } catch (e) {
        console.error('Interval iteration failed', e);
      }
    }, 60000);

    return () => intervalId && clearInterval(intervalId);
  }, []);

  return (
    <AppContext.Provider value={{
      response, setResponse,
      deck, setDeck,
      adBanners, setAdBanners,
      adsEnabled, setAdsEnabled,
      complaintOptions, setComplaintOptions,
      complaintSlideEnabled, setComplaintSlideEnabled,
      registerDevice, // <--- expose unified function
    }}>
      {children}
    </AppContext.Provider>
  );
};
